import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../../environments/environment';
import {Observable, tap} from 'rxjs';
import {CachingInterceptor} from '../../caching/services/caching-interceptor';
import {CmsBlock, CmsEntities, CmsEntity, CmsResult} from '../models/cms-blocks';
import {CmsQuery} from "../models/cms-query";
import * as UrlAssembler from 'url-assembler';
import {map} from "rxjs/operators";

@Injectable()
export class CmsService {

    private readonly cmsResource = '/V1/block/'
    private readonly cmsResourceSearch = '/V1/block/search'

    constructor(private http: HttpClient) {
    }

    private get cmsResourceURL(): string {
        return environment.rest_backend + this.cmsResource;
    }

    private determineCmsSearchResourceUrl(): string {
        return environment.rest_backend + this.cmsResourceSearch;
    }

    getBlock(blockId: string): Observable<CmsBlock> {
        return this.http.get<CmsBlock>(this.cmsResourceURL + blockId, {
            headers: CachingInterceptor.createCacheHeader()
        });
    }

    getBlockList(query: CmsQuery): Observable<CmsResult> {
        const url = this.determineCmsResourceUrlWithQuery(query);
        return this.http.get<CmsEntities>(url, {
            headers: CachingInterceptor.createCacheHeader()
        }).pipe(
            tap(response => console.log('loaded cms blocks', response)),
            map(cmsEntities => this.convertToCmsResult(cmsEntities))
        )
    }

    private convertToCmsResult(cmsEntities: CmsEntities): CmsResult {
        const cmsBlocks = cmsEntities.items.map(cmsEntity => this.convertToCmsBlock(cmsEntity))
        return {cmsBlocks: cmsBlocks, totalCount: cmsEntities.total_count}
    }

    private convertToCmsBlock(cmsEntity: CmsEntity): CmsBlock {
        return {
            id: cmsEntity.id,
            title: cmsEntity.title,
            identifier: cmsEntity.identifier,
            content: cmsEntity.content,
            creationTime: cmsEntity.creation_time,
            updateTime: cmsEntity.update_time,
            active: cmsEntity.active
        }
    }

    private determineCmsResourceUrlWithQuery(query: CmsQuery): string {
        return UrlAssembler(this.determineCmsSearchResourceUrl()).query({
            ...this.constructPageSizeQueryParameter(query),
            ...this.constructCurrentPageQueryParameter(query),
            ...this.constructFilterGroupsParameters(query),
            ...this.constructSortOrderQueryParameters(query)
        }).toString();
    }

    private constructPageSizeQueryParameter(query: CmsQuery) {
        return {
            'searchCriteria[page_size]': query.pageSize
        };
    }

    private constructCurrentPageQueryParameter(query: CmsQuery) {
        return {
            'searchCriteria[current_page]': query.page
        };
    }

    private constructSortOrderQueryParameters(query: CmsQuery) {
        const params = {};
        const sortOrders = query.sortOrders;

        for (let i = 0; i < sortOrders.length; i++) {
            const sortOrder = sortOrders[i];

            params[`searchCriteria[sortOrders][${i}][field]`] = sortOrder.field;
            params[`searchCriteria[sortOrders][${i}][direction]`] = sortOrder.direction;
        }

        return params;
    }

    private constructFilterGroupsParameters(query: CmsQuery) {
        const params = {};
        const filterGroups = query.filterGroups;

        for (let i = 0; i < filterGroups.length; i++) {
            const filterGroup = filterGroups[i];
            const filters = filterGroup.filters;

            for (let j = 0; j < filters.length; j++) {
                const filter = filters[j];

                params[`searchCriteria[filter_groups][${i}][filters][${j}][field]`] = filter.field;
                params[`searchCriteria[filter_groups][${i}][filters][${j}][value]`] = filter.value;
                params[`searchCriteria[filter_groups][${i}][filters][${j}][condition_type]`] = filter.conditionType;
            }
        }

        return params;
    }
}
